/**
 * 🌍 CONFIGURAÇÃO DE AMBIENTE - GESTORCONSIG FRONTEND
 *
 * Centraliza todas as configurações de ambiente para facilitar
 * manutenção e garantir consistência entre desenvolvimento e produção.
 *
 * @author GestorConsig Team
 * @version 2.0.0
 */

// 🔧 Utilitário para obter variáveis de ambiente com fallback
const getEnvVar = (key, defaultValue = null) => {
  return process.env[key] || defaultValue
}

// 🔍 Detecta ambiente atual
const isDevelopment = process.env.NODE_ENV === 'development'
const isProduction = process.env.NODE_ENV === 'production'
const isTest = process.env.NODE_ENV === 'test'

/**
 * 🚀 CONFIGURAÇÕES DA API
 */
export const API_CONFIG = {
  // URLs base
  BASE_URL: getEnvVar('VUE_APP_API_BASE_URL', 'http://localhost:3000'),
  API_URL: getEnvVar('VUE_APP_API_URL', 'http://localhost:3000/api'),
  WEBSOCKET_URL: getEnvVar('VUE_APP_WEBSOCKET_URL', 'ws://localhost:3000'),

  // Timeout e configurações de requisição
  REQUEST_TIMEOUT: parseInt(getEnvVar('VUE_APP_REQUEST_TIMEOUT', '30000')),
  CACHE_TTL: parseInt(getEnvVar('VUE_APP_CACHE_TTL', '300000')),

  // Headers padrão
  DEFAULT_HEADERS: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'X-Client-Version': getEnvVar('VUE_APP_APP_VERSION', '2.0.0')
  }
}

/**
 * 🔐 CONFIGURAÇÕES DE AUTENTICAÇÃO
 */
export const AUTH_CONFIG = {
  // Chaves de armazenamento
  TOKEN_STORAGE_KEY: getEnvVar('VUE_APP_JWT_STORAGE_KEY', 'gestorconsig_token'),
  REFRESH_TOKEN_KEY: getEnvVar(
    'VUE_APP_JWT_REFRESH_KEY',
    'gestorconsig_refresh_token'
  ),

  // URLs de autenticação
  LOGIN_URL: `${API_CONFIG.API_URL}/auth/login`,
  LOGOUT_URL: `${API_CONFIG.API_URL}/auth/logout`,
  REFRESH_URL: `${API_CONFIG.API_URL}/auth/refresh`,
  VERIFY_URL: `${API_CONFIG.API_URL}/auth/verify`,

  // Configurações de sessão
  AUTO_REFRESH_ENABLED: true,
  AUTO_REFRESH_BUFFER: 5 * 60 * 1000, // 5 minutos antes do token expirar
  REMEMBER_ME_DURATION: 7 * 24 * 60 * 60 * 1000 // 7 dias
}

/**
 * 🌐 CONFIGURAÇÕES DE INTEGRAÇÕES
 */
export const INTEGRATION_CONFIG = {
  // Conta Azul OAuth2
  CONTA_AZUL: {
    OAUTH_URL: getEnvVar(
      'VUE_APP_CONTA_AZUL_OAUTH_URL',
      `${API_CONFIG.API_URL}/integrations/contaazul/auth/initiate`
    ),
    CALLBACK_URL: `${API_CONFIG.API_URL}/integrations/contaazul/auth/callback`,
    STATUS_URL: `${API_CONFIG.API_URL}/integrations/contaazul/status`,
    CUSTOMERS_URL: `${API_CONFIG.API_URL}/integrations/contaazul/customers`,
    CONTRACTS_URL: `${API_CONFIG.API_URL}/integrations/contaazul/contracts`
  },

  // Webhooks
  WEBHOOKS: {
    BASE_URL: getEnvVar(
      'VUE_APP_WEBHOOK_BASE_URL',
      `${API_CONFIG.API_URL}/integrations/webhooks`
    ),
    CONTA_AZUL_URL: `${API_CONFIG.API_URL}/integrations/webhooks/contaazul`,
    DISCADOR_URL: `${API_CONFIG.API_URL}/integrations/webhooks/discador`
  }
}

/**
 * 📁 CONFIGURAÇÕES DE UPLOAD
 */
export const UPLOAD_CONFIG = {
  MAX_FILE_SIZE: parseInt(getEnvVar('VUE_APP_MAX_FILE_SIZE', '52428800')), // 50MB
  ALLOWED_FILE_TYPES: getEnvVar(
    'VUE_APP_ALLOWED_FILE_TYPES',
    'csv,txt,zip,rar,mdb,xlsx,xls'
  ).split(','),
  UPLOAD_URL: `${API_CONFIG.API_URL}/uploads`,
  CHUNK_SIZE: 1024 * 1024, // 1MB chunks
  MAX_CONCURRENT_UPLOADS: 3,

  // Validações
  ALLOWED_MIME_TYPES: [
    'text/csv',
    'text/plain',
    'application/zip',
    'application/x-rar-compressed',
    'application/vnd.ms-access',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel'
  ]
}

/**
 * 🎨 CONFIGURAÇÕES DE UI/UX
 */
export const UI_CONFIG = {
  // Informações da aplicação
  APP_NAME: getEnvVar('VUE_APP_APP_NAME', 'GestorConsig'),
  APP_VERSION: getEnvVar('VUE_APP_APP_VERSION', '2.0.0'),

  // Tema
  THEME: {
    PRIMARY: getEnvVar('VUE_APP_THEME_PRIMARY', '#1976d2'),
    SECONDARY: getEnvVar('VUE_APP_THEME_SECONDARY', '#424242')
  },

  // PWA
  PWA: {
    ENABLED: getEnvVar('VUE_APP_PWA_ENABLED', 'true') === 'true',
    NAME: getEnvVar('VUE_APP_PWA_NAME', 'GestorConsig'),
    SHORT_NAME: getEnvVar('VUE_APP_PWA_SHORT_NAME', 'GestorConsig')
  },

  // Localização
  LOCALE: {
    DEFAULT: getEnvVar('VUE_APP_DEFAULT_LOCALE', 'pt-BR'),
    TIMEZONE: getEnvVar('VUE_APP_TIMEZONE', 'America/Sao_Paulo')
  }
}

/**
 * 🎯 FEATURE FLAGS
 */
export const FEATURES = {
  INTEGRATIONS: getEnvVar('VUE_APP_FEATURE_INTEGRATIONS', 'true') === 'true',
  WEBHOOKS: getEnvVar('VUE_APP_FEATURE_WEBHOOKS', 'true') === 'true',
  BOARD_KANBAN: getEnvVar('VUE_APP_FEATURE_BOARD_KANBAN', 'true') === 'true',
  SCHEDULE_SYSTEM:
    getEnvVar('VUE_APP_FEATURE_SCHEDULE_SYSTEM', 'true') === 'true',

  // Features experimentais
  REAL_TIME_NOTIFICATIONS: isDevelopment,
  ADVANCED_ANALYTICS: isProduction,
  BULK_OPERATIONS: true
}

/**
 * 📊 CONFIGURAÇÕES DE MONITORAMENTO
 */
export const MONITORING_CONFIG = {
  // Analytics
  ANALYTICS_ENABLED: getEnvVar('VUE_APP_ENABLE_ANALYTICS', 'false') === 'true',
  GA_TRACKING_ID: getEnvVar('VUE_APP_GA_TRACKING_ID', ''),

  // Sentry
  SENTRY_DSN: getEnvVar('VUE_APP_SENTRY_DSN', ''),
  SENTRY_ENABLED: Boolean(getEnvVar('VUE_APP_SENTRY_DSN', '')),

  // Debug
  DEBUG_MODE:
    getEnvVar('VUE_APP_DEBUG_MODE', isDevelopment.toString()) === 'true',
  LOG_LEVEL: getEnvVar('VUE_APP_LOG_LEVEL', isDevelopment ? 'debug' : 'error')
}

/**
 * 🔧 CONFIGURAÇÕES DE DESENVOLVIMENTO
 */
export const DEV_CONFIG = {
  MOCK_API: getEnvVar('VUE_APP_MOCK_API', 'false') === 'true',
  HOT_RELOAD: isDevelopment,
  SOURCE_MAPS: isDevelopment,

  // URLs de desenvolvimento
  DEV_SERVER_PORT: 8080,
  DEV_SERVER_HOST: 'localhost'
}

/**
 * 🌍 CONFIGURAÇÃO GLOBAL EXPORTADA
 */
export default {
  // Flags de ambiente
  IS_DEVELOPMENT: isDevelopment,
  IS_PRODUCTION: isProduction,
  IS_TEST: isTest,
  NODE_ENV: process.env.NODE_ENV,

  // Configurações principais
  API: API_CONFIG,
  AUTH: AUTH_CONFIG,
  INTEGRATIONS: INTEGRATION_CONFIG,
  UPLOAD: UPLOAD_CONFIG,
  UI: UI_CONFIG,
  FEATURES,
  MONITORING: MONITORING_CONFIG,
  DEV: DEV_CONFIG,

  // Utilitários
  getEnvVar
}

/**
 * 🔍 VALIDAÇÕES DE CONFIGURAÇÃO
 */
if (isDevelopment) {
  console.log('🌍 Environment Configuration Loaded:', {
    NODE_ENV: process.env.NODE_ENV,
    API_URL: API_CONFIG.API_URL,
    FEATURES_ENABLED: Object.keys(FEATURES).filter((key) => FEATURES[key]),
    DEBUG_MODE: MONITORING_CONFIG.DEBUG_MODE
  })
}
